import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Collapse,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import { getImportHistory, downloadImportFile, cancelImport, ImportStatus } from '../services/importService';
import { tokens } from '../theme';

// Status configuration for UI display
const STATUS_CONFIG = {
  [ImportStatus.PENDING]: {
    label: 'En attente',
    color: '#fff3cd',
    textColor: '#856404',
    icon: HourglassEmptyIcon,
  },
  [ImportStatus.PROCESSING]: {
    label: 'En cours',
    color: '#cce5ff',
    textColor: '#004085',
    icon: SyncIcon,
  },
  [ImportStatus.COMPLETED]: {
    label: 'Terminé',
    color: '#d4edda',
    textColor: '#155724',
    icon: CheckCircleIcon,
  },
  [ImportStatus.FAILED]: {
    label: 'Échoué',
    color: '#f8d7da',
    textColor: '#721c24',
    icon: ErrorIcon,
  },
  [ImportStatus.CANCELLED]: {
    label: 'Annulé',
    color: '#e2e3e5',
    textColor: '#383d41',
    icon: CancelIcon,
  },
};

const ImportHistoryDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  
  // Polling interval ref for cleanup
  const pollingRef = useRef(null);

  // Check if any import is in progress
  const hasActiveImport = history.some(
    (item) => item.status === ImportStatus.PENDING || item.status === ImportStatus.PROCESSING
  );

  const fetchHistory = useCallback(async () => {
    try {
      const data = await getImportHistory();
      setHistory(data.history || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement de l\'historique');
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (open) {
      setLoading(true);
      setError(null);
      fetchHistory().finally(() => setLoading(false));
    }
  }, [open, fetchHistory]);

  // Polling for active imports
  useEffect(() => {
    if (open && hasActiveImport) {
      // Start polling every 2 seconds
      pollingRef.current = setInterval(() => {
        fetchHistory();
      }, 2000);
    } else {
      // Stop polling
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [open, hasActiveImport, fetchHistory]);

  const handleDownload = async (historyId) => {
    setDownloading(historyId);
    try {
      await downloadImportFile(historyId);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du téléchargement du fichier');
    } finally {
      setDownloading(null);
    }
  };

  const handleCancel = async (importId) => {
    setCancelling(importId);
    try {
      await cancelImport(importId);
      // Refresh history
      await fetchHistory();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'annulation de l\'import');
    } finally {
      setCancelling(null);
    }
  };

  const handleToggleRow = (rowId) => {
    setExpandedRow(expandedRow === rowId ? null : rowId);
  };

  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchHistory();
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusChip = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG[ImportStatus.COMPLETED];
    const StatusIcon = config.icon;
    
    return (
      <Chip
        icon={<StatusIcon sx={{ fontSize: 16, color: `${config.textColor} !important` }} />}
        label={config.label}
        size="small"
        sx={{
          bgcolor: config.color,
          color: config.textColor,
          fontWeight: 600,
          '& .MuiChip-icon': {
            color: config.textColor,
          },
        }}
      />
    );
  };

  const renderProgressBar = (item) => {
    if (item.status !== ImportStatus.PROCESSING && item.status !== ImportStatus.PENDING) {
      return null;
    }

    const progress = item.progress_percentage || 0;

    return (
      <Box sx={{ mt: 1, width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {item.status_message || 'Traitement en cours...'}
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            {progress}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: colors.grey[200],
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              backgroundColor: colors.primary[500],
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {item.processed_count || 0} / {item.total_rows} contacts
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography variant="caption" sx={{ color: '#155724' }}>
              ✓ {item.success_count || 0}
            </Typography>
            <Typography variant="caption" sx={{ color: '#856404' }}>
              ⊘ {item.skipped_count || 0}
            </Typography>
            <Typography variant="caption" sx={{ color: '#721c24' }}>
              ✗ {item.error_count || 0}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '500px',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 600,
          fontSize: 20,
          borderBottom: `1px solid ${colors.grey[300]}`,
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Historique des importations
          {hasActiveImport && (
            <Chip
              icon={<SyncIcon sx={{ animation: 'spin 1s linear infinite', fontSize: 14 }} />}
              label="Import en cours"
              size="small"
              sx={{
                bgcolor: '#cce5ff',
                color: '#004085',
                fontWeight: 500,
                fontSize: 11,
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Actualiser">
            <IconButton onClick={handleRefresh} size="small" disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : history.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              Aucun historique d'importation disponible
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: `1px solid ${colors.grey[300]}` }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: colors.grey[100] }}>
                  <TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Fichier</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Statut</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Progression</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Importés</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Ignorés</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Erreurs</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item) => (
                  <>
                    <TableRow 
                      key={item.id} 
                      hover
                      sx={{
                        backgroundColor: (item.status === ImportStatus.PROCESSING || item.status === ImportStatus.PENDING) 
                          ? 'rgba(0, 64, 133, 0.03)' 
                          : 'inherit',
                      }}
                    >
                      <TableCell>
                        <IconButton size="small" onClick={() => handleToggleRow(item.id)}>
                          {expandedRow === item.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(item.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {item.filename}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.total_rows} contacts
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {getStatusChip(item.status)}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', minWidth: 100 }}>
                        {item.status === ImportStatus.PROCESSING || item.status === ImportStatus.PENDING ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" fontWeight={600}>
                              {item.progress_percentage || 0}%
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2">100%</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={item.success_count}
                          size="small"
                          sx={{ bgcolor: '#d4edda', color: '#155724', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={item.skipped_count}
                          size="small"
                          sx={{ bgcolor: '#fff3cd', color: '#856404', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={item.error_count}
                          size="small"
                          sx={{
                            bgcolor: item.error_count > 0 ? '#f8d7da' : colors.grey[200],
                            color: item.error_count > 0 ? '#721c24' : 'inherit',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {(item.status === ImportStatus.PROCESSING || item.status === ImportStatus.PENDING) ? (
                            <Tooltip title="Annuler l'import">
                              <IconButton
                                size="small"
                                onClick={() => handleCancel(item.id)}
                                disabled={cancelling === item.id}
                                sx={{
                                  color: '#dc3545',
                                  '&:hover': {
                                    backgroundColor: '#f8d7da',
                                  }
                                }}
                              >
                                {cancelling === item.id ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <CancelIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          ) : (
                        <Tooltip title="Télécharger le fichier CSV">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(item.id)}
                            disabled={downloading === item.id}
                            sx={{
                              color: colors.primary[500],
                              '&:hover': {
                                backgroundColor: colors.primary[100],
                              }
                            }}
                          >
                            {downloading === item.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DownloadIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                        <Collapse in={expandedRow === item.id} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 3, backgroundColor: colors.grey[50] }}>
                            {/* Progress bar for active imports */}
                            {renderProgressBar(item)}
                            
                            {/* Status message */}
                            {item.status_message && (
                              <Box sx={{ mb: 2, mt: renderProgressBar(item) ? 2 : 0 }}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                  Message de statut :
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {item.status_message}
                                </Typography>
                              </Box>
                            )}
                            
                            {/* Field mapping */}
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Mappage des champs utilisé :
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                              {Object.entries(item.field_mapping || {}).map(([csvCol, profileField]) => (
                                profileField !== 'skip' && (
                                  <Chip
                                    key={csvCol}
                                    label={`${csvCol} → ${profileField}`}
                                    size="small"
                                    sx={{
                                      bgcolor: colors.primary[100],
                                      color: colors.primary[700],
                                    }}
                                  />
                                )
                              ))}
                            </Box>
                            
                            {/* Timestamps */}
                            <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
                              {item.started_at && (
                                <Typography variant="caption" color="text.secondary">
                                  Démarré: {formatDate(item.started_at)}
                                </Typography>
                              )}
                              {item.completed_at && (
                                <Typography variant="caption" color="text.secondary">
                                  Terminé: {formatDate(item.completed_at)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: `1px solid ${colors.grey[300]}` }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            px: 4,
            py: 1,
            fontWeight: 600,
            backgroundColor: colors.primary[500],
            '&:hover': {
              backgroundColor: colors.primary[600],
            }
          }}
        >
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportHistoryDialog;
